import React from 'react';
import CurrencyFormat from 'react-currency-format';
import {DirectionalHint, Icon, Text, TooltipHost, useTheme} from '@fluentui/react';
import {INormalizedProduct} from '../../models/NormalizedProduct';
import {IVendor} from '../../models/Search';
import {useGetAllVendorSettingsQuery} from '../../store/Api';
import {renderVendorNameFromAllSettings} from '../../logic/Product';

interface IVendorPeekProps {
    product?: INormalizedProduct;
    /** Vendor to mark as the one behind the figure being hovered (the selected source or best vendor). */
    highlight?: string;
    /** Skip the settings lookup (customize-columns examples). */
    isExample?: boolean;
}

const MAX_PEEK_VENDORS = 8;

const stockLabel = (v: IVendor): string => {
    if (v.onHand != null && v.onHand > 0) return `${v.onHand} on hand`;
    return v.inStock ? 'In stock' : 'No stock';
};

/**
 * Hovering a Cost or Best Price cell peeks at the vendors behind it: each vendor's cost and
 * availability, cheapest first, with the vendor the figure came from marked. Products with no
 * vendor rows render their children untouched.
 */
const VendorPeek: React.FC<IVendorPeekProps> = ({product, highlight, isExample, children}) => {
    const theme = useTheme();
    const settings = useGetAllVendorSettingsQuery(undefined, {skip: isExample || !product?.vendors?.length});
    const vendors = product?.vendors ?? [];
    if (vendors.length === 0) return <>{children}</>;

    const sorted = [...vendors].sort((a, b) => (a.cost ?? Number.MAX_VALUE) - (b.cost ?? Number.MAX_VALUE));
    const shown = sorted.slice(0, MAX_PEEK_VENDORS);
    const more = sorted.length - shown.length;
    const isHighlight = (v: IVendor) => !!highlight && v.vendorName.toLowerCase() === highlight.toLowerCase();

    const peek = (
        <div style={{minWidth: 240}}>
            <Text variant='small' block styles={{root: {fontWeight: 600, marginBottom: 6}}}>Vendor cost and availability</Text>
            <table style={{borderCollapse: 'collapse', width: '100%'}}>
                <tbody>
                    {shown.map((v, index) => {
                        const inStock = v.inStock || (v.onHand ?? 0) > 0;
                        // A vendor can carry the same part more than once (separate price or stock rows), so the
                        // name and part number alone are not unique. The list is rebuilt whole each render.
                        return (
                            <tr key={`${v.vendorName}-${v.vendorPartNumber}-${index}`} style={{fontWeight: isHighlight(v) ? 700 : 400}}>
                                <td style={{padding: '2px 8px 2px 0', whiteSpace: 'nowrap'}}>{renderVendorNameFromAllSettings(v.vendorName, settings.data)}</td>
                                <td style={{padding: '2px 8px', textAlign: 'right', whiteSpace: 'nowrap'}}>
                                    {v.cost ? <CurrencyFormat value={v.cost} displayType='text' thousandSeparator=',' prefix='$' decimalScale={2} fixedDecimalScale /> : 'N/A'}
                                </td>
                                <td style={{padding: '2px 0 2px 8px', whiteSpace: 'nowrap', color: inStock ? theme.palette.themePrimary : theme.palette.neutralSecondary}}>
                                    {stockLabel(v)}
                                </td>
                                <td style={{padding: '2px 0 2px 6px', width: 16}}>
                                    {v.url && (
                                        <a href={v.url} target='_blank' rel='noopener noreferrer' title='Open at the vendor'
                                           onClick={(e) => e.stopPropagation()} style={{color: theme.palette.themePrimary, display: 'inline-flex'}}>
                                            <Icon iconName='NavigateExternalInline' styles={{root: {fontSize: 11}}} />
                                        </a>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {more > 0 && <Text variant='small' block styles={{root: {color: theme.palette.neutralSecondary, marginTop: 6}}}>+{more} more</Text>}
        </div>
    );

    return (
        <TooltipHost content={peek} directionalHint={DirectionalHint.leftCenter} calloutProps={{gapSpace: 6}} styles={{root: {display: 'block', width: '100%'}}}>
            {children}
        </TooltipHost>
    );
};

export default VendorPeek;
